// ---- Item specs ----
// A pick-up-able item is data. This file is the SHAPE (the ItemSpec type + the
// weapon/wear/ranged sub-blocks); the actual items are CONTENT — `content/items/
// <type>.json` (stats + blocks) + `content/models/<type>.json` (geometry), hydrated
// into `./index.ts`'s `ITEMS`. The `ItemType` union is GENERATED from content/items/
// (src/content/item-types.ts), so there is nothing to hand-edit in shared.ts.
//
// Add an item: edit content/items/<type>.json + content/models/, then `bun run gen`
// (regenerates the manifest + the ItemType union) — see content/SCHEMA.md. The
// inventory is SERVER-OWNED (index.ts + src/server/bag.ts); the client HUD is a mirror.

import * as THREE from "three";
import { makePrims, type Prims } from "../meshkit.ts";
import type { ItemType, WearSlot } from "../shared.ts";
import type { LootTable } from "../loot.ts";
import type { WeaponArchetypeId } from "../anim/archetypes.ts";
import { codedWearDef } from "../wear/index.ts";

/** How the held weapon is swung when it attacks. Picks the pose override in
 *  player.ts (see startAttack / updatePlayer). `draw` is the ranged nock-and-loose
 *  motion (a bow) — it plays exactly like the melee swings but fires a projectile
 *  instead of resolving reach. */
export type WeaponStyle = "swing" | "chop" | "thrust" | "draw";
/** One-handed (rides the main hand) vs two-handed (the off-hand also grips it). */
export type WeaponGrip = "one-hand" | "two-hand";

/** One distance tier of a ranged weapon: any shot whose xz distance to the target
 *  is ≤ `max` (and greater than the previous band's max) hits with probability
 *  `hit` (0..1). The LAST band's `max` doubles as the hard fire cap — beyond it the
 *  shot can't be loosed at all. */
export type RangeBand = { max: number; hit: number };

/** The data that makes a weapon *ranged* (a bow, a future crossbow, an NPC
 *  archer's attack). Its presence flips combat.ts from a melee swing to a fired
 *  projectile. Deliberately attacker-agnostic — the same block drives the player's
 *  bow and, later, an enemy's ranged attack, so the projectile system stays shared.
 *  A ranged weapon still carries the base WeaponSpec fields (`damage` per hit,
 *  `attackCooldown`, `staminaCost`, `style:"draw"`); `attackRange` is the hard cap
 *  and should equal the last band's `max`. */
export type RangedSpec = {
  /** ItemType consumed from the bag per shot (e.g. "wooden-arrow"). The SERVER
   *  decrements it in the `fire` handler (authoritative bag); the client only gates
   *  the UX on having ≥1 so it won't send a doomed shot. */
  ammo: ItemType;
  /** Distance→hit-chance tiers, ASCENDING by `max`. First matching band wins. */
  bands: RangeBand[];
  /** Cosmetic flight speed of the projectile (world units / second). */
  projectileSpeed: number;
};

/** The combat data that makes an item a *weapon*. Mirrors the NPC `combat` block:
 *  an item is equippable-as-a-weapon iff its spec carries one of these. Add a new
 *  weapon = a model file + a `weapon` block + an `archetype` (shared attack/stance
 *  set — see `src/anim/archetypes.ts`); nothing else in the system changes. */
export type WeaponSpec = {
  /** Animation archetype this weapon uses. Attack/carry/ready/hold-override are
   *  shared by every item of the same archetype (`content/anim/clips.json`
   *  `archetypes.<id>`). Per-item `hold.hilt`/`rot` still seat THIS mesh. */
  archetype: WeaponArchetypeId;
  /** HP removed per landed hit. */
  damage: number;
  /** Seconds between swings (the spear is deliberately slow). */
  attackCooldown: number;
  /** Stamina spent per swing. */
  staminaCost: number;
  /** Melee reach on the xz plane (world units). */
  attackRange: number;
  /** Two-hand weapons pose the off-hand onto the shaft (see player.ts). Combat
   *  mechanics only — animation comes from `archetype`. */
  grip: WeaponGrip;
  /** Combat light-attack style (cleave arc / ranged). Animation comes from
   *  `archetype`, not this field. */
  style: WeaponStyle;
  /** Present ⇒ this is a *ranged* weapon: attacking fires a projectile (consuming
   *  `ranged.ammo`) instead of resolving melee reach. `attackRange` is the hard
   *  cap (= last band's max). See combat.ts (client) + index.ts (server roll). */
  ranged?: RangedSpec;
  /** Skill box id that must be unlocked to equip this weapon type (Plan 09 weapon-type
   *  gate). Server-checked on equip; DOM-free so index.ts reads it via itemSpec(). */
  requiresBox?: string;
  /** Special-move ids (content/moves) this weapon supports — action-bar slots
   *  2-3 (plan 14). Slot 1's light attack is this weapon block itself. Server-
   *  validated on use (supported + the move's own requiresBox unlocked). */
  moves?: string[];
  /**
   * How the model seats in the hand socket once equipped.
   *
   * **Grip seat (locked):**
   * - Model owns `landmarks.grip` (handle center in local model space).
   * - `hilt` MUST equal `landmarks.grip`. `seatWeapon` pins that point to the
   *   hand socket origin for ANY `rot` — so grip only needs setting once on
   *   the model, and every item that uses the model copies it into hilt.
   *
   * **Orientation (one source of truth):**
   * - `rot` on the **item** is the BASE fist orientation for THIS mesh. Prefer
   *   fixing it here — not via clips hold override or a huge stance/clip
   *   `grip` delta.
   * - clips `archetypes.<id>.hold` may rarely override rot/offset/scale (never
   *   hilt) for the whole archetype when anim truly needs a different base.
   * - stance/clip `grip` is a small DELTA on rot for one pose (e.g. lay blade
   *   forward for a slash) — never a full re-seat.
   *
   * **offset:** tiny styling nudge AFTER hilt→socket alignment (prefer 0).
   * Large offsets pull the real grip off the fist; validate warns.
   */
  hold: {
    hilt: [number, number, number];
    rot: [number, number, number];
    scale?: number;
    offset?: [number, number, number];
  };
};

/** The data that makes an item *wearable* on a humanoid (mirrors the `weapon`
 *  block pattern): an item is equippable-to-a-body-slot iff its spec carries one
 *  of these. Rendering is a doc merge at build time (player.ts mergeWear):
 *  `part` fills the slot's anchor slot(s) with a content/parts doc (armor/hats/
 *  packs — also the ground-drop model when the item has no `model`), `recolor`
 *  overrides body palette keys (cloth), `body` deep-merges body params (short
 *  sleeves / shorts). `armor` is the server-authoritative damage reduction. */
export type WearSpec = {
  /** Body area — one worn item per slot (head/torso/legs/feet/back). */
  slot: WearSlot;
  /** % damage reduction while worn; summed across gear, capped server-side. */
  armor: number;
  /** content/parts doc worn at the slot's anchor(s). */
  part?: string;
  /** Coded wearable id (src/wear) — custom geometry, not a parts doc. */
  coded?: string;
  /** Palette-key → "#rrggbb" overrides on the body while worn. */
  recolor?: Record<string, string>;
  /** Humanoid body params deep-merged while worn. */
  body?: Record<string, unknown>;
};

/** The data that makes an item a SHIELD (plan 14 — same presence-is-the-switch
 *  pattern as `weapon`/`wear`): equips to the OFF hand (handOff socket),
 *  only alongside a one-hand weapon (a two-hander locks the offhand). While
 *  held up (the block intent), it soaks `block`% of FRONTAL damage and drains
 *  `staminaPerHit` per blocked hit; an empty pool staggers the blocker open. */
export type ShieldSpec = {
  /** % of frontal damage soaked while blocking (0-100). */
  block: number;
  /** Stamina drained per blocked hit — running dry = staggered open. */
  staminaPerHit: number;
  /** Skill box that must be unlocked to equip it (plan-09 gate). */
  requiresBox?: string;
  /** How the model seats in the off fist — same shape as WeaponSpec.hold. */
  hold: WeaponSpec["hold"];
};

/** The data that makes an item a FISHING ROD (plan 10 — same presence-is-the-
 *  switch pattern as `weapon`/`wear`/`shield`): equippable to the held-tool
 *  channel (`NetPlayer.tool`, transient like `offhand`), seated in the handMain
 *  socket, and the enabler for the `cast` intent. Pacing defaults live in
 *  src/sim/fishing.ts (CAST_RANGE / the encounter consts); a rod only
 *  overrides what differs. */
export type FishingSpec = {
  /** Max reach to a valid water cast point (default CAST_RANGE 12). */
  castRange?: number;
  /** × the fish notice roll — a better rod draws fish sooner (default 1). */
  attractMul?: number;
  /** Skill box that must be unlocked to equip this rod tier (plan-09 gate). */
  requiresBox?: string;
  /** How the rod seats in the main-hand socket — same shape as WeaponSpec.hold. */
  hold: WeaponSpec["hold"];
};

/** The data that makes an item a HATCHET (tree-cutting tool — same presence-is-
 *  the-switch pattern as `fishing`): equippable to the held-tool channel
 *  (`NetPlayer.tool`), seated in handMain, and the enabler for `chopStart`.
 *  Animation rides the `hatchet` weapon archetype (carry/ready/attack clips). */
export type ChoppingSpec = {
  /** Skill box that must be unlocked to equip this hatchet tier (future gate). */
  requiresBox?: string;
  /** How the hatchet seats in the main-hand socket — same shape as WeaponSpec.hold. */
  hold: WeaponSpec["hold"];
};

/** Species log item types (campfire fuel + tree-cutting yield). */
export const LOG_ITEM_TYPES = ["oak-logs", "pine-logs", "birch-logs"] as const;
export type LogItemType = (typeof LOG_ITEM_TYPES)[number];
export function isLogType(t: string): t is LogItemType {
  return (LOG_ITEM_TYPES as readonly string[]).includes(t);
}

/** The data that makes an item a CATCHABLE FISH (Living Waters rework — same
 *  presence-is-the-switch pattern): it lives in a map doc's `waters[]`
 *  population, swims as a visible shadow, runs the notice → approach →
 *  investigate → nibble → bite encounter, and IS the catch when landed.
 *  Structurally identical to the sim's FishParams (src/sim/fishing.ts) — the
 *  spec block is passed straight into the machines, no adapter. Composes with
 *  `cook` (raw fish are cookable); excludes every equip/deploy block. */
export type FishSpec = {
  /** Hook tier — bites only when the angler's folded tableTier ≥ this. */
  tier: number;
  /** Shadow/model scale × (default 1) — proportional to the fish's size. */
  size?: number;
  /** u/s while wandering its water (default sim CRUISE_SPEED 0.8). */
  cruiseSpeed?: number;
  /** u/s burst when spooked (default sim DART_SPEED 2.4). */
  dartSpeed?: number;
  /** 0..1 notice-roll base — 0 timid, 1 bold (default sim CURIOSITY 0.6). */
  curiosity?: number;
  /** Seconds of investigate budget base (default sim PATIENCE 5). */
  patience?: number;
  /** Nibble tugs before the bite (defaults: tunables nibbleMin/nibbleMax). */
  nibblesMin?: number;
  nibblesMax?: number;
  /** Reel-fight difficulty × — scales arrow count AND pace (default 1). */
  fightMul?: number;
};

/** The data that makes an item a DEPLOYABLE FISHING NET (plan 10): set at the
 *  water's edge (consumed/anchored as a server-owned deployed object with an
 *  absolute soak timer), collected later for a bulk haul (the net returns to
 *  the bag). Never held — no `hold`. */
export type NetSpec = {
  /** Seconds the deployed net soaks before it's ready (default SOAK_TIME 540). */
  soakTime?: number;
  /** Net tier — reserved for CP2 bulk-table tiers (default 0). */
  tier?: number;
  /** Skill box that must be unlocked to deploy this net tier (plan-09 gate). */
  requiresBox?: string;
};

/** The marker that makes a ranged weapon a HUNTING BOW (plan 11 — same
 *  presence-is-the-switch pattern as `weapon`/`fishing`): it rides the normal
 *  weapon equip + `fire` path, but its presence (a) routes a prey kill to
 *  Hunting/stalking XP at the kill site (the tool-gate — a war-bow kill stays
 *  Combat), (b) raises the over-the-shoulder aim camera while drawn, and
 *  (c) folds its bonuses with the Stalking box grants (huntingSum). Requires
 *  `weapon.ranged` (schema-enforced). */
export type HuntingSpec = {
  /** Flat + to the rolled distance-band hit% (capped at the sim's AIM_HIT_CAP). */
  aimHitBonus?: number;
  /** + trophy-tier access from the bow itself (CP2 bow tiers; sums with boxes). */
  trophyTier?: number;
  /** Skill box that must be unlocked to equip this bow tier (plan-09 gate). */
  requiresBox?: string;
};

/** The data that makes an item a DEPLOYABLE TRAP (plan 11 — the fishing-net
 *  shape on land): set on huntable ground (consumed/anchored as a server-owned
 *  deployed object with an absolute soak timer), collected later for a bulk
 *  meat/hide haul (the trap returns to the bag). Never held — no `hold`. The
 *  `haul` loot table lives on the item itself (no new content domain). */
export type TrapSpec = {
  /** Seconds the deployed trap soaks before it's ready (default TRAP_SOAK 480). */
  soakTime?: number;
  /** Small game per haul, uniform roll (defaults TRAP_CAPACITY_MIN/MAX 2-4). */
  capacityMin?: number;
  capacityMax?: number;
  /** Trap tier — reserved for CP2 haul-table tiers (default 0). */
  tier?: number;
  /** Skill box that must be unlocked to deploy this trap tier (plan-09 gate). */
  requiresBox?: string;
  /** The per-small-game roll (rolled capacity× per collect via rollLoot). */
  haul: LootTable;
};

/** The data that makes an item a DEPLOYABLE FERMENTER (plan 18 CP3, Brewing
 *  branch): set on dry ground, it soaks on a durable timer and yields a bulk
 *  brewed haul when collected — the TrapSpec shape exactly (Brewing is Trapping's
 *  twin on the Cooking skill). The fermenter is returned to the bag on collect. */
export type BrewSpec = {
  /** Seconds the deployed fermenter soaks before it's ready (default BREW_SOAK 420). */
  soakTime?: number;
  /** Brewed goods per haul, uniform roll (defaults BREW_CAPACITY_MIN/MAX 3-6). */
  capacityMin?: number;
  capacityMax?: number;
  /** Fermenter tier — reserved for later haul-table tiers (default 0). */
  tier?: number;
  /** Skill box that must be unlocked to deploy this fermenter tier (plan-09 gate). */
  requiresBox?: string;
  /** The per-bottle roll (rolled capacity× per collect via rollLoot). */
  haul: LootTable;
};

/** The data that makes an item EDIBLE (plan 18 — same presence-is-the-switch
 *  pattern): consuming one unit heals HP + restores stamina, then removes it from
 *  the bag. The server owns the HP heal (authoritative, via the `vitals` path);
 *  stamina is client-predicted like the rest of the stamina model. Cooked dishes
 *  carry this; raws don't. */
export type FoodSpec = {
  /** HP restored per unit eaten (server-authoritative; clamped to maxHp). */
  heal?: number;
  /** Stamina restored per unit eaten (client-predicted + server-gate bumped). */
  stamina?: number;
};

/** The marker that makes a raw item COOKABLE (plan 18): cooking it at a hearth
 *  yields `into` (its cooked product). Only the output mapping lives here — the
 *  per-ingredient cook time + XP are CONTENT in content/tunables/cooking.json
 *  (COOKING_TUNING, keyed by the RAW item type), the one home for that pacing, so
 *  there's no second source to drift. Presence enables the `cook` intent. */
export type CookSpec = {
  /** The item type produced by a successful cook (a real ItemType — the edible dish). */
  into: ItemType;
};

/** Combat armor quality rung (content tag — not a weight class). */
export type ArmorTier = "basic" | "trimmed" | "elite";

export type ItemSpec = {
  /** Stable id — must be a member of the ItemType union in shared.ts. */
  type: ItemType;
  /** Display name, shown when a slot is selected (e.g. "Iron Sword"). */
  name: string;
  /** Slot glyph for the inventory HUD (an emoji). */
  icon: string;
  /** Click/hover hit padding (world units) — small props need a forgiving ray. */
  radius: number;
  /**
   * content/models id for this item's mesh (and landmarks.grip seat). Omitted
   * for part-only wearables whose ground drop is the worn part geometry.
   */
  model?: string;
  /** Combat armor quality rung (basic/trimmed/elite). Omit on non-combat wearables. */
  tier?: ArmorTier;
  /** Draw the model from mesh-kit prims, feet at y=0 (+z = south, like decos). */
  build: (p: Prims) => void;
  /** Present ⇒ this item can be equipped and used as a weapon in combat. */
  weapon?: WeaponSpec;
  /** Present ⇒ this item can be worn in a humanoid body slot (see WearSpec). */
  wear?: WearSpec;
  /** Present ⇒ this item is an offhand SHIELD (see ShieldSpec, plan 14). */
  shield?: ShieldSpec;
  /** Present ⇒ this item is a FISHING ROD (held-tool channel, plan 10). */
  fishing?: FishingSpec;
  /** Present ⇒ this item is a HATCHET (held-tool channel, tree-cutting). */
  chopping?: ChoppingSpec;
  /** Present ⇒ this item is a deployable FISHING NET (plan 10). */
  net?: NetSpec;
  /** Present ⇒ this ranged weapon is a HUNTING BOW (plan 11 tool-gate marker). */
  hunting?: HuntingSpec;
  /** Present ⇒ this item is a deployable TRAP (plan 11). */
  trap?: TrapSpec;
  /** Deployable fermenter (plan 18 CP3 — the Brewing branch). */
  brew?: BrewSpec;
  /** Present ⇒ this item is EDIBLE (a cooked dish, plan 18). */
  food?: FoodSpec;
  /** Present ⇒ this raw item is COOKABLE into `cook.into` at a hearth (plan 18). */
  cook?: CookSpec;
  /** Present ⇒ this item is a CATCHABLE FISH living in map waters[] (Living Waters). */
  fish?: FishSpec;
  /** Max units that fit in one inventory slot (a "stack"). Default 1 (unstackable
   *  — weapons/tools). Ammo/consumables set this high (e.g. arrows: 99). */
  stack?: number;
  /** Units granted per world pickup — one arrow prop hands you a quiver's worth.
   *  Default 1. Clamped to `stack` when pocketed. */
  bundle?: number;
  /** Dev-studio only (src/studio): optional per-frame animation for the model
   *  viewer, receiving the built+normalized group. Ignored by the game and the
   *  headless server. */
  studioAnimate?: (o: THREE.Object3D, dt: number, t: number) => void;
};

export function defineItem(spec: ItemSpec): ItemSpec {
  return spec;
}

/** True (and narrows the type) when an item spec is equippable as a weapon. */
export function isWeapon(spec: ItemSpec): spec is ItemSpec & { weapon: WeaponSpec } {
  return spec.weapon !== undefined;
}

/** True (and narrows) when an item spec is wearable in a body slot. */
export function isWearable(spec: ItemSpec): spec is ItemSpec & { wear: WearSpec } {
  return spec.wear !== undefined;
}

/** True (and narrows) when an item spec is an offhand shield (plan 14). */
export function isShield(spec: ItemSpec): spec is ItemSpec & { shield: ShieldSpec } {
  return spec.shield !== undefined;
}

/** True (and narrows) when an item spec is a fishing rod (plan 10). Both sides
 *  use it: the client to gate the cast UX, the server to validate `equip`+`cast`. */
export function isFishing(spec: ItemSpec): spec is ItemSpec & { fishing: FishingSpec } {
  return spec.fishing !== undefined;
}

/** True (and narrows) when an item spec is a hatchet (tree-cutting tool). */
export function isChopping(spec: ItemSpec): spec is ItemSpec & { chopping: ChoppingSpec } {
  return spec.chopping !== undefined;
}

/** True when the item equips on the held-tool channel (rod or hatchet). */
export function isHeldTool(spec: ItemSpec): boolean {
  return isFishing(spec) || isChopping(spec);
}

/** True (and narrows) when an item spec is a deployable fishing net (plan 10). */
export function isNet(spec: ItemSpec): spec is ItemSpec & { net: NetSpec } {
  return spec.net !== undefined;
}

/** True (and narrows) when an item spec is a HUNTING BOW (plan 11): a ranged
 *  weapon carrying the `hunting` marker. Both sides use it — the client to
 *  raise the aim camera, the server to tool-gate the kill-site XP route. */
export function isHuntingBow(
  spec: ItemSpec,
): spec is ItemSpec & { hunting: HuntingSpec; weapon: WeaponSpec & { ranged: RangedSpec } } {
  return spec.hunting !== undefined && spec.weapon?.ranged !== undefined;
}

/** True (and narrows) when an item spec is a deployable trap (plan 11). */
export function isTrap(spec: ItemSpec): spec is ItemSpec & { trap: TrapSpec } {
  return spec.trap !== undefined;
}

/** True (and narrows) when an item spec is a deployable fermenter (plan 18 CP3). */
export function isBrew(spec: ItemSpec): spec is ItemSpec & { brew: BrewSpec } {
  return spec.brew !== undefined;
}

/** True (and narrows) when an item spec is edible (plan 18). Both sides use it:
 *  the client to show the Eat affordance, the server to validate `consume`. */
export function isFood(spec: ItemSpec): spec is ItemSpec & { food: FoodSpec } {
  return spec.food !== undefined;
}

/** True (and narrows) when a raw item can be cooked into another (plan 18). The
 *  client gates the Cook affordance with it; the server validates the `cook` intent. */
export function isCookable(spec: ItemSpec): spec is ItemSpec & { cook: CookSpec } {
  return spec.cook !== undefined;
}

/** True (and narrows) when an item spec is a catchable FISH (Living Waters).
 *  The server spawns water populations from it; the studio filters species
 *  pickers with it; the client reads size for the shadow scale. */
export function isFish(spec: ItemSpec): spec is ItemSpec & { fish: FishSpec } {
  return spec.fish !== undefined;
}

/** True (and narrows) when a weapon spec fires a projectile (has a `ranged` block).
 *  Both sides use it: the client to fire, the server to roll the hit. */
export function isRanged(
  w: WeaponSpec,
): w is WeaponSpec & { ranged: RangedSpec } {
  return w.ranged !== undefined;
}

/** The distance band that decides a shot's hit chance: first band whose max
 *  covers `d`, else the farthest (the hard-cap fallback). THE one copy of the
 *  band-selection rule — the player `fire` handler, NPC archers, and the
 *  studio arena's tracer stub all pick through it. */
export function bandFor(bands: RangeBand[], d: number): RangeBand {
  return bands.find((b) => d <= b.max) ?? bands[bands.length - 1]!;
}

/** Max stack size for an item type's slot (default 1 — unstackable). */
export function stackSize(spec: ItemSpec): number {
  return spec.stack ?? 1;
}

/** Run an item's build() and return a Group (feet at y=0) ready to position. */
export function buildItem(spec: ItemSpec): THREE.Group {
  const group = new THREE.Group();
  // Coded wearables (open cloaks, …) may only have freeform ground geometry —
  // skip the prim path when the registry owns the drop.
  if (spec.wear?.coded && !spec.model) {
    const def = codedWearDef(spec.wear.coded);
    if (def) {
      def.buildGround(group);
      return group;
    }
  }
  const push = (mesh: THREE.Mesh) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  };
  // Seed 0: items are unique placed props, not scattered, so no per-instance
  // variation is needed (the blob salt only matters for duplicated rocks).
  spec.build(makePrims(push, 0));
  return group;
}
